/* StylinArts PDP Wishlist
 * - Works with Cloudflare Worker at https://wishlist-api.stylinarts.com/_wishlist
 * - Stores variant (size/colour) preferences in localStorage (PREFS_KEY)
 * - Keeps button state ("Save to wishlist" / "Saved ✓")
 * - Safe on all pages (only runs where [data-sa-wishlist] exists)
 */

(() => {
  "use strict";

  const API_BASE = "https://wishlist-api.stylinarts.com/_wishlist";
  const PREFS_KEY = "saWishlistPrefsV1";
  const ROOT_SEL = "[data-sa-wishlist]";
  const BTN_SEL = ".sa-wishlist-btn";
  const MSG_SEL = ".sa-wishlist-msg";

  let initPromise = null;

  /* ---------- localStorage helpers (shared with wishlist page) ---------- */

  function loadPrefs() {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  }

  function savePrefs(obj) {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(obj));
    } catch {
      // ignore quota errors
    }
  }

  function saveVariantPreference(key, pref) {
    if (!key || !pref) return;
    const all = loadPrefs();
    all[String(key)] = pref;
    savePrefs(all);
  }

  /* ---------- Worker API helpers ---------- */

  async function ensureInit() {
    if (initPromise) return initPromise;

    initPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/init`, {
          method: "POST",
          credentials: "include"
        });
        if (!res.ok) {
          // Don't throw hard; PDP should still render, button will just fail gracefully
          console.warn("Wishlist init failed", res.status);
        }
      } catch (e) {
        console.warn("Wishlist init error", e);
      }
    })();

    return initPromise;
  }

  async function apiToggle(product_id, product_handle) {
    await ensureInit();

    const body = {
      product_id: Number(product_id),
      product_handle: String(product_handle || "").trim()
    };

    const res = await fetch(`${API_BASE}/toggle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      throw new Error(`Wishlist toggle failed (${res.status})`);
    }

    // Worker returns { status, count, items: [...] }
    const payload = await res.json().catch(() => ({}));
    return payload;
  }

  /* ---------- Variant capture (for prefs only) ---------- */

  function captureVariantPref(root, productId, handle) {
    // Find the nearest add-to-cart form
    const form =
      root.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!form) return;

    // Variant ID (standard Shopify: input/select name="id")
    let variantId = null;
    const idField =
      form.querySelector('input[name="id"]') ||
      form.querySelector('select[name="id"]');

    if (idField && idField.value) {
      const num = Number(idField.value);
      if (Number.isFinite(num)) variantId = num;
    }

    // Collect option labels (best-effort, theme-agnostic)
    const options = {};
    const optionInputs = form.querySelectorAll(
      '[name^="options["], [name^="option"], [data-product-attribute]'
    );

    optionInputs.forEach((el) => {
      const name =
        el.getAttribute("data-product-attribute") ||
        el.getAttribute("name") ||
        "";
      if (!name) return;

      let val = "";
      if (el.tagName === "SELECT") {
        val = el.value;
      } else if (
        (el.type === "radio" || el.type === "checkbox") &&
        !el.checked
      ) {
        return;
      } else {
        val = el.value;
      }

      if (!val) return;
      options[name] = val;
    });

    const key = productId || handle;
    if (!key) return;

    saveVariantPreference(key, {
      variant_id: variantId,
      options
    });
  }

  /* ---------- UI helpers ---------- */

  function setBtnState(btn, isOn) {
    if (!btn) return;
    btn.classList.toggle("is-on", !!isOn);
    btn.setAttribute("aria-pressed", isOn ? "true" : "false");
    btn.textContent = isOn ? "Saved ✓" : "Save to wishlist";
  }

  function setBtnLoading(btn, isLoading) {
    if (!btn) return;
    btn.disabled = !!isLoading;
    btn.classList.toggle("is-loading", !!isLoading);
  }

  function setMessage(root, text) {
    const box = root.querySelector(MSG_SEL);
    if (!box) return;
    if (!text) {
      box.style.display = "none";
      box.textContent = "";
    } else {
      box.style.display = "";
      box.textContent = text;
    }
  }

  /* ---------- Click handler ---------- */

  async function handleClick(root, btn) {
    const productIdRaw = btn.getAttribute("data-product-id") || "";
    const productHandle = btn.getAttribute("data-product-handle") || "";
    const product_id = productIdRaw ? Number(productIdRaw) : null;

    if (!Number.isFinite(product_id) || !productHandle) {
      console.error("Wishlist: missing product_id or product_handle");
      setMessage(root, "Failed. Please try again later.");
      return;
    }

    // Capture current variant preference BEFORE we toggle
    captureVariantPref(root, product_id, productHandle);

    setBtnLoading(btn, true);
    setMessage(root, "");

    try {
      const payload = await apiToggle(product_id, productHandle);

      // Decide whether this product is now in the wishlist
      let isOn = false;
      if (payload && Array.isArray(payload.items)) {
        isOn = payload.items.some((it) => {
          const pid = Number(it.product_id || it.id);
          const handle =
            it.product_handle || it.handle || it.slug || "";
          return (
            Number.isFinite(pid) &&
            pid === product_id &&
            handle === productHandle
          );
        });
      }

      setBtnState(btn, isOn);

      // Refresh header badge if available
      if (typeof window.saWishlistHeaderRefresh === "function") {
        window.saWishlistHeaderRefresh();
      }
    } catch (e) {
      console.error(e);
      setMessage(root, "Failed. Please try again later.");
    } finally {
      setBtnLoading(btn, false);
    }
  }

  /* ---------- Boot ---------- */

  function bootOne(root) {
    const btn = root.querySelector(BTN_SEL);
    if (!btn) return;

    // Initial state: assume "off"; we don't fetch on PDP for now to save calls
    setBtnState(btn, false);
    setMessage(root, "");

    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      handleClick(root, btn);
    });
  }

  function bootAll() {
    const roots = document.querySelectorAll(ROOT_SEL);
    if (!roots.length) return;
    roots.forEach(bootOne);
  }

  document.addEventListener("DOMContentLoaded", bootAll);
  document.addEventListener("shopify:section:load", bootAll);
  document.addEventListener("shopify:section:select", bootAll);
})();
